import type { Degree } from "@pdfking/math"
import { AddMargins, Center, Scale, Subtract } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { PageShape } from "@pdfking/renderer-api"
import { GroupMatrix, PlotFunction } from "@pdfking/renderer-api"
import { FullMethodDispatcher } from "@pdfking/tfunc"

import { OVERSHOOT, PALETTE } from "#constants"
import type { CalendarArea, PageContext } from "#internal"

import { Reach } from "../compose/Composition"
import { DrawBadge } from "../layout/DrawBadge"
import { DrawPerforation } from "../layout/DrawPerforation"
import { PageMetrics } from "../layout/PageMetrics"
import { PlotNotebookBackground } from "../layout/PlotNotebookBackground"
import { PlainText } from "../text/PlainText"
import { NotebookBackground } from "../types/NotebookBackground"
import { CalendarImage } from "./CalendarImage"
import { DrawContact } from "./DrawContact"
import { DrawDayList } from "./DrawDayList"
import { DrawDayStrip } from "./DrawDayStrip"
import { DrawFooter } from "./DrawFooter"
import { DrawMonthGrid } from "./DrawMonthGrid"

// A line of text as tall as its box, or turned to read upwards
const line = ({
  align,
  box,
  color,
  text,
  vertical,
}: Extract<CalendarArea, { enum: "text" }>): PageShape[] => {
  // As tall as the box, its round letters' overshoot within it
  const font = Scale(box.height, 1 - OVERSHOOT)
  if (!vertical) return [PlainText(text, box.x, box.width, box.y, font, color, align)]
  // Set along the box's height, turned a quarter about its centre
  const center = Center(box)
  return [
    {
      enum: PlotFunction.PLOT_GROUP,
      matrix: GroupMatrix({ origin: center, rotation: 90 as Degree }),
      shapes: [
        PlainText(
          text,
          Subtract(center.x, Scale(box.height, 0.5)),
          box.height,
          Subtract(center.y, Scale(box.width, 0.5)),
          Scale(box.width, 1 - OVERSHOOT),
          color,
          align
        ),
      ],
    },
  ]
}

/** A calendar's page of `size` filled area by area, on its paper running on
 * into `bleed`, as is any area's edge on the page's: every page of every
 * calendar is its `areas`, wherever its design puts them, its pictures
 * CalendarImage's, as `page` has it; its footer drawn when `footer`, the
 * year set again in the spot when `spot_year`, a picture's text over it as
 * well. */
export const GenerateCalendarLayout = ({
  areas,
  footer,
  page: { bleed, labels, language, seed, size },
  spot_year,
}: {
  areas: CalendarArea[]
  footer: boolean
  page: PageContext
  spot_year: boolean
}): PageShape[] => {
  const { short } = PageMetrics(size)
  const draw = FullMethodDispatcher<[CalendarArea], PageShape[]>({
    badge: ({ box, lines, spot }) => DrawBadge({ box, lines, spot }),
    contact: ({ box, right, solid }) => DrawContact({ box, labels, right, solid }),
    footer: ({ box }) => (footer ? DrawFooter({ box, labels }) : []),
    days: ({ aligned, box, first, last, month, rows, year }) =>
      DrawDayList({ aligned, box, first, language, last, month, rows, year }),
    grid: ({ box, layout, month, year }) => DrawMonthGrid({ box, language, layout, month, year }),
    picture: ({ box, is_year, over, round, salt }) =>
      CalendarImage({
        bleed: Reach({ bleed, size }, box, 0),
        box,
        over:
          spot_year && is_year
            ? [
                ...over,
                ...over.flatMap(shape =>
                  shape.enum === PlotFunction.PLOT_TEXT
                    ? [{ ...shape, text_color: PALETTE.spot }]
                    : []
                ),
              ]
            : over,
        round,
        salt,
        seed,
        short,
      }),
    perforation: ({ box }) =>
      DrawPerforation({ box: AddMargins(box, Reach({ bleed, size }, box, 0)) }),
    rules: ({ box, pitch }) =>
      PlotNotebookBackground({ background: NotebookBackground.LINES, box, pitch }),
    solid: ({ box, color }) => [
      {
        enum: PlotFunction.PLOT_RECT,
        fill_color: color,
        position: AddMargins(box, Reach({ bleed, size }, box, 0)),
      },
    ],
    strip: ({ box, month, ruled, year }) => DrawDayStrip({ box, language, month, ruled, year }),
    text: area =>
      spot_year && area.is_year
        ? [...line(area), ...line({ ...area, color: PALETTE.spot })]
        : line(area),
  })
  const page = { ...size, x: ZERO_DISTANCE, y: ZERO_DISTANCE }
  return [
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.paper, position: AddMargins(page, bleed) },
    ...areas.flatMap(area => draw(area)),
  ]
}
